import React, { useMemo, useCallback, useState, useEffect } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { useInBreakpoints as isBreakpoint } from 'js/design_system/hooks/useBreakpoint';

import { Paper, OpenalexWork, PaperImage } from 'js/social/shared/types';
import { Carousel } from 'js/social/shared/Carousel';

import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';
import _PaperImagesContainer from "PaperImages-PaperImagesContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=PaperImagesContainer";
import _SlideContainer from "PaperImages-SlideContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=SlideContainer";
import _SlideCaption from "PaperImages-SlideCaption.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=SlideCaption";
import _SlideImageContainer from "PaperImages-SlideImageContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=SlideImageContainer";
import _SlideImageImpression from "PaperImages-SlideImageImpression.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=SlideImageImpression";
import _SlideImage from "PaperImages-SlideImage.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=SlideImage";
import _Citation from "PaperImages-Citation.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperImages.tsx?styleId=Citation";


type Props = {
  work: Paper | OpenalexWork;
  canUseReader: boolean;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  paperInteractions: React.ReactNode;
  onImageChange: (imageIndex: number) => void;
  variant: 'default' | 'dense';
};

const PaperImagesContainer = /*#__PURE__*/styled("div", null, {
  displayName: "PaperImagesContainer",
  styles: _PaperImagesContainer
});

const SlideContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SlideContainer",
  styles: _SlideContainer
});

const SlideCaption = /*#__PURE__*/styled(Body, null, {
  displayName: "SlideCaption",
  styles: _SlideCaption
});

const SlideImageContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SlideImageContainer",
  styles: _SlideImageContainer
});

const SlideImageImpression = /*#__PURE__*/styled(ImpressionWatcher, null, {
  displayName: "SlideImageImpression",
  styles: _SlideImageImpression
});

const SlideImage = /*#__PURE__*/styled("img", null, {
  displayName: "SlideImage",
  styles: _SlideImage
});

const Citation = /*#__PURE__*/styled(Body, null, {
  displayName: "Citation",
  styles: _Citation
});

const CarouselSlide = ({
  item,
  index,
  clickableProps,
  impressionPropsGen,
}: {
  item: PaperImage;
  index: number;
  clickableProps: object;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
}) => {
  return (
    <SlideContainer>
      <SlideCaption size="sm">{item.caption}</SlideCaption>
      <SlideImageContainer>
        <SlideImageImpression {...impressionPropsGen(`slide-${index}`)}>
          <SlideImage src={item.url} alt={item.caption} {...clickableProps} />
        </SlideImageImpression>
      </SlideImageContainer>
    </SlideContainer>
  );
};

const PaperImages = ({
  work,
  canUseReader,
  showReader,
  impressionPropsGen,
  paperInteractions,
  onImageChange = () => null,
  variant = 'default',
}: Props) => {
  const clickableProps = useMemo(() => {
    return canUseReader
      ? {
          onClick: showReader,
        }
      : { href: work.url, target: '_blank' };
  }, [canUseReader, showReader, work]);

  const renderItem = useCallback(
    (item: PaperImage, index: number) => (
      <CarouselSlide {...{ item, index, clickableProps, impressionPropsGen }} />
    ),
    [clickableProps, impressionPropsGen],
  );

  const [arrows, setArrows] = useState(isBreakpoint(['medium', 'large']));

  useEffect(() => {
    const handleResize = () => setArrows(isBreakpoint(['medium', 'large']));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const images = work.images || [];
  if (images.length === 0) return null;

  return (
    <PaperImagesContainer variant={variant}>
      <Carousel
        items={images}
        renderItem={renderItem}
        gap={0}
        keyExtractor={(item) => item.url}
        sliderProps={{
          infinite: false,
          lazyLoad: 'ondemand',
          arrows,
          afterChange: onImageChange,
        }}
      />
      <Citation>Source: {work.citation}</Citation>
      {paperInteractions}
    </PaperImagesContainer>
  );
};

export default PaperImages;
